/**
 * Utilidades de debug para verificar el estado de los datos
 */

import { Storage } from '@capacitor/storage';

/**
 * Verifica el estado completo de los datos almacenados
 */
export const debugStorageState = async (): Promise<void> => {
  console.log('🔍 === DEBUG: Estado del Storage ===');
  
  try {
    // Verificar datos de usuario
    const userKeys = [
      'hasLoggedIn',
      'hasSeenTutorial', 
      'username',
      'userced',
      'userid',
      'userother',
      'eventcode',
      'eventName',
      'eventStartDate',
      'eventEndDate',
      'eventLocation'
    ];
    
    console.log('👤 Datos de Usuario:');
    for (const key of userKeys) {
      const { value } = await Storage.get({ key });
      console.log(`  ${key}: ${value}`);
    }
    
    // Verificar datos de conferencia
    const confKeys = [
      'dataLoaded',
      'scheduleData',
      'speakersData', 
      'locationsData',
      'lastUpdate'
    ];
    
    console.log('\n📅 Datos de Conferencia:');
    for (const key of confKeys) {
      const { value } = await Storage.get({ key });
      if (key === 'scheduleData' || key === 'speakersData' || key === 'locationsData') {
        // Para datos grandes, solo mostrar si existen y su tamaño
        if (value) {
          try {
            const parsed = JSON.parse(value);
            console.log(`  ${key}: ✅ (${JSON.stringify(parsed).length} chars)`);
          } catch {
            console.log(`  ${key}: ❌ (invalid JSON)`);
          }
        } else {
          console.log(`  ${key}: ❌ (null)`);
        }
      } else {
        console.log(`  ${key}: ${value}`);
      }
    }
    
    // Verificar favoritos
    const { value: favorites } = await Storage.get({ key: 'FAVORITOS' });
    console.log(`\n⭐ Favoritos: ${favorites || 'ninguno'}`);
    
  } catch (error) {
    console.error('❌ Error al verificar storage:', error);
  }
  
  console.log('🔍 === FIN DEBUG ===\n');
};

/**
 * Limpia todos los datos de conferencia (útil para testing)
 */
export const clearConferenceData = async (): Promise<void> => {
  console.log('🧹 Limpiando datos de conferencia...');
  
  const keysToRemove = [
    'dataLoaded',
    'scheduleData',
    'speakersData',
    'locationsData',
    'lastUpdate'
  ];
  
  try {
    for (const key of keysToRemove) {
      await Storage.remove({ key });
    }
    console.log('✅ Datos de conferencia limpiados');
  } catch (error) {
    console.error('❌ Error al limpiar datos:', error);
  }
};

/**
 * Limpia todos los datos de usuario (útil para testing)
 */
export const clearUserData = async (): Promise<void> => {
  console.log('🧹 Limpiando datos de usuario...');
  
  const keysToRemove = [
    'hasLoggedIn',
    'hasSeenTutorial',
    'username',
    'userced', 
    'userid',
    'userother',
    'eventcode',
    'eventName',
    'eventStartDate',
    'eventEndDate',
    'eventLocation',
    'FAVORITOS'
  ];
  
  try {
    for (const key of keysToRemove) {
      await Storage.remove({ key });
    }
    console.log('✅ Datos de usuario limpiados');
  } catch (error) {
    console.error('❌ Error al limpiar datos de usuario:', error);
  }
};

/**
 * Fuerza la recarga de datos de conferencia
 */
export const forceReloadConferenceData = async (): Promise<void> => {
  console.log('🔄 Forzando recarga de datos de conferencia...');
  
  try {
    // Limpiar datos existentes
    await clearConferenceData();
    
    // Importar y usar el servicio de carga de datos
    const { default: DataLoadingService } = await import('../services/DataLoadingService');
    const dataLoadingService = DataLoadingService.getInstance();
    
    // Obtener código de evento
    const { value: eventCode } = await Storage.get({ key: 'eventcode' });
    
    if (!eventCode || eventCode === 'nada') {
      console.log('❌ No hay código de evento válido');
      return;
    }
    
    console.log(`🎯 Cargando datos para evento: ${eventCode}`);
    
    // Forzar recarga
    const result = await dataLoadingService.loadEventData(eventCode, true);
    
    if (result.success) {
      console.log('✅ Datos recargados exitosamente');
      console.log(`📊 Resumen:`, {
        hasSchedule: result.hasSchedule,
        hasSpeakers: result.hasSpeakers,
        hasLocations: result.hasLocations,
        sessionsCount: result.sessions?.length || 0,
        speakersCount: result.speakers?.length || 0,
        locationsCount: result.locations?.length || 0
      });
    } else {
      console.log('❌ Error al recargar datos');
    }
    
  } catch (error) {
    console.error('❌ Error durante la recarga forzada:', error);
  }
};

/**
 * Verifica la conectividad y estado de la API
 */
export const checkApiConnectivity = async (): Promise<void> => {
  console.log('🌐 Verificando conectividad API...');
  
  try {
    const { value: eventCode } = await Storage.get({ key: 'eventcode' });
    
    if (!eventCode || eventCode === 'nada') {
      console.log('❌ No hay código de evento para verificar');
      return;
    }
    
    // Verificar endpoint de datos
    const dataUrl = `https://esimposio.com/miesimposio/appendpoints/${eventCode}/?type=data&uid=123456`;
    const locationsUrl = `https://esimposio.com/miesimposio/appendpoints/${eventCode}/?type=locations&uid=123456`;
    
    console.log(`🎯 Verificando endpoints para evento: ${eventCode}`);
    
    // Test de conectividad
    const startTime = Date.now();
    
    try {
      const [dataResponse, locationsResponse] = await Promise.all([
        fetch(dataUrl),
        fetch(locationsUrl)
      ]);
      
      const endTime = Date.now();
      const responseTime = endTime - startTime;
      
      console.log(`⏱️ Tiempo de respuesta: ${responseTime}ms`);
      console.log(`📊 Estado de respuestas:`, {
        data: `${dataResponse.status} ${dataResponse.statusText}`,
        locations: `${locationsResponse.status} ${locationsResponse.statusText}`
      });
      
      if (dataResponse.ok) {
        const dataJson = await dataResponse.json();
        console.log(`📅 Datos recibidos:`, {
          hasSchedule: !!dataJson.schedule,
          hasSpeakers: !!dataJson.speakers,
          scheduleLength: dataJson.schedule?.length || 0,
          speakersLength: dataJson.speakers?.length || 0
        });
      }
      
      if (locationsResponse.ok) {
        const locationsJson = await locationsResponse.json();
        console.log(`📍 Ubicaciones recibidas:`, {
          hasLocations: !!locationsJson.locations,
          locationsLength: locationsJson.locations?.length || 0
        });
      }
      
    } catch (fetchError) {
      console.error('❌ Error de conectividad:', fetchError);
    }
    
  } catch (error) {
    console.error('❌ Error al verificar API:', error);
  }
};

// Exportar funciones para uso en DevTools
if (typeof window !== 'undefined') {
  (window as any).debugStorage = debugStorageState;
  (window as any).clearConferenceData = clearConferenceData;
  (window as any).clearUserData = clearUserData;
  (window as any).forceReloadConferenceData = forceReloadConferenceData;
  (window as any).checkApiConnectivity = checkApiConnectivity;
}
